import { useState, type PropsWithChildren } from "react";
import { Link, NavLink } from "react-router-dom";

import lockupUrl from "../../../../assets/brand/packful-lockup-horizontal.svg";
import { PlusIcon } from "./icons";
import { Button } from "./ui";

export function BrandRail() {
  return (
    <div className="brand-rail" aria-hidden="true">
      <span />
      <span />
      <span />
      <span />
      <span />
      <span />
    </div>
  );
}

export function PublicFrame({ children }: PropsWithChildren) {
  return (
    <main className="public-frame">
      <BrandRail />
      <section className="public-frame__panel">
        <img className="public-frame__lockup" src={lockupUrl} alt="Packful" />
        {children}
      </section>
      <aside className="public-frame__aside" aria-hidden="true">
        <p>CONTROL SURFACE</p>
        <strong>Organisation provisioning</strong>
        <span>Authorised Packful operators only</span>
      </aside>
    </main>
  );
}

export function AppShell({
  children,
  operatorEmail,
  onSignOut,
}: PropsWithChildren<{
  operatorEmail: string;
  onSignOut: () => Promise<void>;
}>) {
  const [signingOut, setSigningOut] = useState(false);

  async function signOut() {
    if (signingOut) return;
    setSigningOut(true);
    try {
      await onSignOut();
    } finally {
      setSigningOut(false);
    }
  }

  return (
    <div className="app-frame">
      <header className="app-header">
        <Link to="/" className="app-header__brand" aria-label="Packful operator console home">
          <img src={lockupUrl} alt="Packful" />
          <span>Operator console</span>
        </Link>
        <div className="app-header__identity">
          <span className="environment-badge">
            {import.meta.env.VITE_PACKFUL_ENVIRONMENT}
          </span>
          <span className="operator-email">{operatorEmail}</span>
          <Button tone="quiet" busy={signingOut} onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      </header>
      <div className="app-body">
        <aside className="side-nav" aria-label="Operator navigation">
          <div className="side-nav__marker"><BrandRail /></div>
          <nav>
            <NavLink end to="/" className={({ isActive }) => isActive ? "active" : undefined}>
              Organisations
            </NavLink>
            <NavLink to="/organisations/new" className={({ isActive }) => isActive ? "active" : undefined}>
              <PlusIcon />
              New organisation
            </NavLink>
          </nav>
          <div className="side-nav__scope">
            <span>Scope</span>
            <p>Organisation identity and first-administrator onboarding only.</p>
          </div>
        </aside>
        <main className="app-main">{children}</main>
      </div>
    </div>
  );
}

export function PageHeader({
  actions,
  eyebrow,
  title,
  children,
}: PropsWithChildren<{
  actions?: React.ReactNode;
  eyebrow: string;
  title: string;
}>) {
  return (
    <header className="page-header">
      <div>
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        {children ? <div className="page-header__description">{children}</div> : null}
      </div>
      {actions ? <div className="page-header__actions">{actions}</div> : null}
    </header>
  );
}
